// Blog Service - Handles API calls for blog functionality
// This will be updated when the database is implemented

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? 'https://droshpod.com/api' 
    : 'http://localhost:5001/api'
);

class BlogService {
  // Get all blog posts
  async getAllPosts(includeUnpublished = false) {
    try {
      const url = includeUnpublished 
        ? `${API_BASE_URL}/blog?published=all` 
        : `${API_BASE_URL}/blog`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      const data = await response.json();
      return data.posts || data;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      throw error;
    }
  }

  // Get a single blog post by ID
  async getPostById(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/${id}`);
      if (!response.ok) {
        throw new Error('Blog post not found');
      }
      const data = await response.json();
      return data.post || data;
    } catch (error) {
      console.error('Error fetching blog post:', error);
      throw error;
    }
  }

  // Get blog posts by category
  async getPostsByCategory(category) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog-posts/category/${category}`);
      if (!response.ok) {
        throw new Error('Failed to fetch posts by category');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching posts by category:', error);
      throw error;
    }
  }

  // Search blog posts
  async searchPosts(query) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog-posts/search?q=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error('Failed to search posts');
      }
      return await response.json();
    } catch (error) {
      console.error('Error searching posts:', error);
      throw error;
    }
  }

  // Get featured blog posts
  async getFeaturedPosts() {
    try {
      const response = await fetch(`${API_BASE_URL}/blog-posts/featured`);
      if (!response.ok) {
        throw new Error('Failed to fetch featured posts');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching featured posts:', error);
      throw error;
    }
  }

  // Get related posts (same category, excluding current post)
  async getRelatedPosts(postId, category, limit = 3) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog-posts/related/${postId}?category=${category}&limit=${limit}`);
      if (!response.ok) {
        throw new Error('Failed to fetch related posts');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching related posts:', error);
      throw error;
    }
  }

  // Get blog categories
  async getCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/blog-categories`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  // Create a new blog post (admin only)
  async createPost(postData) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create blog post');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error;
    }
  }

  // Update a blog post (admin only)
  async updatePost(id, postData) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update blog post');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  }

  // Delete a blog post (admin only)
  async deletePost(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete blog post');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  }

  // Upload blog post image (admin only)
  async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/blog-posts/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: formData
      });
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      return await response.json();
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  // Subscribe to newsletter
  async subscribeToNewsletter(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      if (!response.ok) {
        throw new Error('Failed to subscribe to newsletter');
      }
      return await response.json();
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }

  // Get blog post analytics (admin only)
  async getPostAnalytics(postId) {
    try {
      const response = await fetch(`${API_BASE_URL}/blog-posts/${postId}/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch post analytics');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching post analytics:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const blogService = new BlogService();
export default blogService; 